import createTxpDataRow from './createTxpDataRow';
import populateInvoiceBusStopData from './populateInvoiceBusStopData';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import CardItem from '../../schema/viewSchema/CardItem';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import Invoice from '../../schema/Invoice';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpStrings from '../../schema/configSchema/TxpStrings';

export default function createInvoiceCard(
    invoiceEntity: Invoice,
    isBusStop: boolean,
    activityType: ActivityType,
    activityId: string,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons
): CardItem {
    let detailsDataFirstLine: string;
    let ariaDetailsFormatString: string;
    let paymentDateAriaFormat: string;
    let ariaDetailsText: string;
    let paymentDate: string;
    let brandSummarySecondLine: string;

    let customerText = invoiceEntity.customer ? invoiceEntity.customer.name : '';
    let isInvoiceScheduled = invoiceEntity.scheduledPaymentDate ? true : false;

    let amount =
        invoiceEntity.totalPaymentDue && invoiceEntity.totalPaymentDue.price
            ? invoiceEntity.totalPaymentDue.price
            : null;
    if (amount) {
        ariaDetailsFormatString = isInvoiceScheduled
            ? txpStrings.strings.invoiceAriaWithScheduledFormatText
            : txpStrings.strings.invoiceAriaWithDueFormatText;
    } else {
        ariaDetailsFormatString = isInvoiceScheduled
            ? txpStrings.strings.invoiceAriaWithScheduledNoAmountFormatText
            : txpStrings.strings.invoiceAriaWithDueNoAmountFormatText;
    }

    let paymentDatePrefix = isInvoiceScheduled
        ? txpStrings.strings.invoiceScheduledText
        : txpStrings.strings.invoiceDueText;

    if (isInvoiceScheduled) {
        paymentDate = txpStrings.formatters.formatWeekDayDate(invoiceEntity.scheduledPaymentDate);
        brandSummarySecondLine = txpStrings.formatters.format(
            txpStrings.strings.brandSummaryInvoiceSecondLineText,
            amount,
            txpStrings.formatters.formatUserDate(invoiceEntity.scheduledPaymentDate)
        );
        paymentDateAriaFormat = txpStrings.formatters.formatWeekDayMonthDayYear(
            invoiceEntity.scheduledPaymentDate
        );
    } else {
        paymentDate = txpStrings.formatters.formatWeekDayDate(invoiceEntity.paymentDueDate);
        brandSummarySecondLine = txpStrings.formatters.format(
            txpStrings.strings.brandSummaryInvoiceSecondLineText,
            amount,
            txpStrings.formatters.formatUserDate(invoiceEntity.paymentDueDate)
        );
        paymentDateAriaFormat = txpStrings.formatters.formatWeekDayMonthDayYear(
            invoiceEntity.paymentDueDate
        );
    }

    detailsDataFirstLine = paymentDate;

    ariaDetailsText =
        txpStrings.formatters.format(ariaDetailsFormatString, paymentDateAriaFormat, amount) +
        ' ' +
        customerText;

    const actionButtonData =
        invoiceEntity.potentialAction && invoiceEntity.potentialAction.url
            ? {
                  actionIconName: detailsIcons.paymentCard,
                  actionUrl: invoiceEntity.potentialAction.url,
                  actionButtonText: txpStrings.strings.payBillButtonText,
                  actionName: ActionNameConstants.PayBill,
              }
            : null;

    let busStopData: BusStopData;
    if (isBusStop) {
        busStopData = populateInvoiceBusStopData(
            invoiceEntity,
            activityType,
            amount,
            paymentDate,
            paymentDateAriaFormat,
            customerText,
            txpStrings,
            detailsIcons.paymentCard
        );
    }

    return {
        id: activityId,
        entityIcon: entityIcon,
        entityImage: entityImage,
        activityType: activityType,
        entityType: EntityType.Invoice,
        summaryData: {
            firstLine: {
                text: txpStrings.formatters.format(
                    txpStrings.strings.invoiceBillText,
                    invoiceEntity.provider.name
                ),
            },
            secondLine: {
                text: txpStrings.formatters.format(
                    invoiceEntity.accountIdEndsIn
                        ? txpStrings.strings.invoiceAccountNumberEndingInText
                        : txpStrings.strings.invoiceAccountNumberText,
                    invoiceEntity.accountIdEndsIn || invoiceEntity.accountId
                ),
            },
        },
        detailsData: {
            firstLine: createTxpDataRow(
                detailsDataFirstLine,
                paymentDatePrefix,
                detailsIcons.clock
            ),
            secondLine: customerText
                ? createTxpDataRow(
                      customerText,
                      txpStrings.strings.invoiceCustomerText,
                      detailsIcons.contact
                  )
                : null,
            thirdLine: amount
                ? createTxpDataRow(amount, txpStrings.strings.invoiceAmountText, detailsIcons.money)
                : null,
        },
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
        ariaText: ariaDetailsText,
        busStopData: busStopData,
        brandsSummaryFirstLine: txpStrings.strings.brandSummaryInvoiceFirstLineText,
        brandsSummarySecondLine: brandSummarySecondLine,
    } as CardItem;
}
